/**
 * CallRecordingButton — compact "listen" control for any call listing.
 *
 * Features
 *   • Headphone icon button; click opens a Radix Popover with:
 *       - WaveSurfer waveform (interactive scrubbing)
 *       - Play/Pause + Speed toggle
 *       - "Download file" direct-to-Yabbit link
 *       - "Open detail" deeplink to the full CallDetailPage
 *   • Optional `startTime` (seconds) prop auto-seeks + auto-plays on open
 *     — powers timestamped deep-links from DISC evidence snippets.
 *   • Empty-state renders a "Re-fetch from Yabbit" button (managers only)
 *     that hits GET /api/calls/{id}/recording?force=true to backfill.
 *   • Recording URL is lazy-fetched only on first open.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { Headphones, Download, ExternalLink, Loader2, AudioLines, Play, Pause, RotateCcw, Gauge } from 'lucide-react';
import WaveSurfer from 'wavesurfer.js';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPEEDS = [1, 1.25, 1.5, 2];

export const CallRecordingButton = ({
  callId,
  size = 'sm',
  label,
  startTime = null,   // seconds — auto-seek+play when set
  className = '',
  onOpenChange,
}) => {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refetching, setRefetching] = useState(false);
  const [err, setErr] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [current, setCurrent] = useState(0);
  const [total, setTotal] = useState(0);

  const containerRef = useRef(null);
  const wsRef = useRef(null);
  const fetched = useRef(false);
  const { user } = useAuth();
  const canRefetch = ['master_admin', 'manager', 'supervisor', 'domain_admin', 'domain_supervisor']
    .includes(user?.role);

  const loadRecording = useCallback(async (force = false) => {
    setLoading(true);
    setErr(null);
    try {
      const r = await axios.get(
        `${API}/calls/${callId}/recording${force ? '?force=true' : ''}`,
      );
      setUrl(r.data?.recording_url || null);
      if (force && r.data?.refetched) toast.success('Recording fetched from Yabbit.');
      if (force && !r.data?.recording_url) toast.info('Yabbit still has no recording for this call.');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'No recording available');
    } finally {
      setLoading(false);
      setRefetching(false);
    }
  }, [callId]);

  useEffect(() => {
    if (!open || fetched.current) return;
    fetched.current = true;
    loadRecording();
  }, [open, loadRecording]);

  // Initialise WaveSurfer once the container + url are both ready
  useEffect(() => {
    if (!open || !url || !containerRef.current) return;

    // Tear down any previous instance
    if (wsRef.current) { try { wsRef.current.destroy(); } catch (_) { /* noop */ } wsRef.current = null; }

    const ws = WaveSurfer.create({
      container: containerRef.current,
      url,
      height: 44,
      waveColor: '#94A3B8',
      progressColor: '#0D9488',
      cursorColor: '#0F766E',
      barWidth: 2,
      barGap: 1,
      barRadius: 2,
      normalize: true,
      backend: 'MediaElement',   // reuse a native audio element for streaming
      mediaControls: false,
    });
    wsRef.current = ws;

    ws.on('ready', () => {
      const dur = ws.getDuration() || 0;
      setTotal(dur);
      ws.setPlaybackRate(speed, true);
      if (typeof startTime === 'number' && startTime >= 0 && dur > 0) {
        const pos = Math.min(startTime, Math.max(0, dur - 1));
        ws.setTime(pos);
        ws.play().catch(() => { /* autoplay policy */ });
      }
    });
    ws.on('play', () => setPlaying(true));
    ws.on('pause', () => setPlaying(false));
    ws.on('finish', () => setPlaying(false));
    ws.on('timeupdate', (t) => setCurrent(t));
    ws.on('error', () => setErr('Recording could not be played — the link may have expired.'));

    return () => {
      try { ws.destroy(); } catch (_) { /* noop */ }
      wsRef.current = null;
    };
  }, [open, url, startTime]);

  // Pause when the popover closes
  useEffect(() => {
    if (!open && wsRef.current) {
      try { wsRef.current.pause(); } catch (_) { /* noop */ }
    }
  }, [open]);

  const iconSize = size === 'xs' ? 'w-3 h-3' : size === 'md' ? 'w-4 h-4' : 'w-3.5 h-3.5';
  const btnPad = size === 'xs' ? 'p-1' : size === 'md' ? 'p-2' : 'px-1.5 py-1';

  const togglePlay = () => {
    if (!wsRef.current) return;
    if (wsRef.current.isPlaying()) wsRef.current.pause();
    else wsRef.current.play().catch(() => { /* noop */ });
  };
  const restart = () => { if (wsRef.current) { wsRef.current.setTime(0); wsRef.current.play().catch(() => {}); } };
  const cycleSpeed = () => {
    const next = SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length];
    setSpeed(next);
    if (wsRef.current) wsRef.current.setPlaybackRate(next, true);
  };

  const fmt = (s) => {
    if (!s || !isFinite(s)) return '0:00';
    const m = Math.floor(s / 60); const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); onOpenChange?.(o); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className={`inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-teal-50 hover:text-teal-700 hover:border-teal-300 transition-colors ${btnPad} ${className}`}
          title="Listen to this call"
          aria-label="Listen to this call"
          data-testid={`listen-btn-${callId}`}
        >
          <Headphones className={iconSize} />
          {label && <span className="text-[11px] font-semibold">{label}</span>}
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        className="w-96 p-3 space-y-2"
        onClick={(e) => e.stopPropagation()}
        data-testid={`listen-popover-${callId}`}
      >
        <div className="flex items-center justify-between">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 inline-flex items-center gap-1.5">
            <AudioLines className="w-3 h-3 text-teal-600" /> Call recording
          </p>
          <a
            href={`/calls/${callId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] font-semibold text-slate-500 hover:text-teal-700 inline-flex items-center gap-1"
            data-testid={`listen-open-detail-${callId}`}
          >
            Open detail <ExternalLink className="w-2.5 h-2.5" />
          </a>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-6 text-slate-500 text-xs">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
          </div>
        )}

        {!loading && err && (
          <p className="text-xs text-rose-600 italic py-2">{err}</p>
        )}

        {!loading && !err && !url && (
          <div className="py-2 space-y-2">
            <p className="text-xs text-slate-500 italic">
              No recording is stored for this call yet.
            </p>
            {canRefetch && (
              <button
                type="button"
                onClick={() => { setRefetching(true); loadRecording(true); }}
                disabled={refetching}
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-teal-700 hover:text-teal-900 disabled:opacity-60"
                data-testid={`listen-refetch-${callId}`}
              >
                {refetching ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                {refetching ? 'Fetching…' : 'Try fetching from Yabbit'}
              </button>
            )}
          </div>
        )}

        {!loading && url && (
          <>
            <div
              ref={containerRef}
              className="rounded-lg bg-slate-50 border border-slate-200 px-2 py-1.5"
              data-testid={`listen-waveform-${callId}`}
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={togglePlay}
                className="p-2 rounded-full bg-teal-600 hover:bg-teal-700 text-white shrink-0"
                data-testid={`listen-play-${callId}`}
                aria-label={playing ? 'Pause' : 'Play'}
              >
                {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              </button>
              <button
                type="button"
                onClick={restart}
                className="p-1.5 rounded-full text-slate-500 hover:bg-slate-100"
                title="Restart"
                aria-label="Restart"
                data-testid={`listen-restart-${callId}`}
              >
                <RotateCcw className="w-3.5 h-3.5" />
              </button>
              <span className="text-[11px] font-mono tabular-nums text-slate-600 shrink-0">
                {fmt(current)} <span className="text-slate-300">/</span> {fmt(total)}
              </span>
              <div className="flex-1" />
              <button
                type="button"
                onClick={cycleSpeed}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border border-slate-200 text-[10px] font-semibold text-slate-600 hover:bg-slate-50"
                title="Playback speed"
                data-testid={`listen-speed-${callId}`}
              >
                <Gauge className="w-3 h-3" /> {speed}×
              </button>
              <a
                href={url}
                download={`recording-${callId}.wav`}
                className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 hover:text-teal-700"
                title="Download file"
                data-testid={`listen-download-${callId}`}
                onClick={(e) => e.stopPropagation()}
              >
                <Download className="w-3.5 h-3.5" />
              </a>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default CallRecordingButton;
